import { useState } from 'react';
import { villageLifePosts } from '@/mocks/community';

export default function VillageLifeSection() {
  const [likedIds, setLikedIds] = useState<Set<string>>(new Set());
  const [activePost, setActivePost] = useState<string | null>(null);

  const toggleLike = (id: string) => {
    setLikedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-green-100 rounded-full px-4 py-1.5 mb-4">
            <i className="ri-landscape-line text-green-600 text-sm"></i>
            <span className="text-green-700 text-sm font-semibold">农村生活分享</span>
          </div>
          <h2 className="text-3xl font-black text-gray-900 mb-3">看见不一样的世界</h2>
          <p className="text-gray-500 text-base max-w-2xl mx-auto">
            农村学生分享他们的日常生活，让城市同学了解另一种成长环境，学会感恩，珍惜当下
          </p>
        </div>

        {/* Inspiration Banner */}
        <div className="bg-gradient-to-r from-green-700 to-emerald-700 rounded-3xl p-6 md:p-8 mb-10 text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <img
              src="https://public.readdy.ai/ai/img_res/d9d4b3ab967d20b3d51fddca79d3cd87.jpg"
              alt="农村风景"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="relative z-10 max-w-2xl">
            <h3 className="text-2xl font-black mb-3">每一个孩子的梦想都值得被看见</h3>
            <p className="text-white/80 text-sm leading-relaxed mb-4">
              他们每天走山路上学，在煤油灯下做作业，用最简陋的条件追逐最远大的梦想。
              城市的同学们，看看他们的故事，你会更加珍惜现在拥有的一切，也会更加感恩父母的付出。
            </p>
            <div className="flex flex-wrap gap-3">
              <div className="flex items-center gap-2 bg-white/20 rounded-full px-4 py-2">
                <i className="ri-walk-line text-white/80"></i>
                <span className="text-sm">平均每天步行 40分钟 上学</span>
              </div>
              <div className="flex items-center gap-2 bg-white/20 rounded-full px-4 py-2">
                <i className="ri-book-2-line text-white/80"></i>
                <span className="text-sm">全国仍有 1,200万 农村留守儿童</span>
              </div>
            </div>
          </div>
        </div>

        {/* Posts Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {villageLifePosts.map((post) => (
            <div key={post.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:border-green-200 transition-all">
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={post.image}
                  alt={post.content.slice(0, 20)}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                <div className="absolute bottom-3 left-4 flex items-center gap-2">
                  <img src={post.avatar} alt={post.author} className="w-8 h-8 rounded-full object-cover object-top border-2 border-white" />
                  <div>
                    <p className="text-white font-bold text-sm">{post.author}</p>
                    <p className="text-white/80 text-xs">{post.region} · {post.grade}</p>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-5">
                <p className="text-gray-700 text-sm leading-relaxed mb-4">
                  {activePost === post.id ? post.content : post.content.slice(0, 80) + (post.content.length > 80 ? '...' : '')}
                  {post.content.length > 80 && (
                    <button
                      onClick={() => setActivePost(activePost === post.id ? null : post.id)}
                      className="text-green-600 text-xs ml-1 cursor-pointer hover:underline"
                    >
                      {activePost === post.id ? '收起' : '展开'}
                    </button>
                  )}
                </p>

                <div className="flex flex-wrap gap-1.5 mb-4">
                  {post.tags.map((tag) => (
                    <span key={tag} className="text-xs bg-green-50 text-green-700 px-2.5 py-1 rounded-full">#{tag}</span>
                  ))}
                </div>

                <div className="flex items-center justify-between text-gray-400 text-xs">
                  <span>{post.time}</span>
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => toggleLike(post.id)}
                      className={`flex items-center gap-1 cursor-pointer transition-colors ${likedIds.has(post.id) ? 'text-rose-500' : 'hover:text-rose-500'}`}
                    >
                      <i className={`text-base ${likedIds.has(post.id) ? 'ri-heart-fill' : 'ri-heart-line'}`}></i>
                      {post.likes + (likedIds.has(post.id) ? 1 : 0)}
                    </button>
                    <button className="flex items-center gap-1 cursor-pointer hover:text-green-500 transition-colors">
                      <i className="ri-chat-3-line text-base"></i> {post.comments}
                    </button>
                    <button className="flex items-center gap-1 cursor-pointer hover:text-blue-500 transition-colors">
                      <i className="ri-share-forward-line text-base"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Gratitude Message */}
        <div className="mt-10 bg-amber-50 rounded-3xl p-6 md:p-8 text-center border border-amber-100">
          <div className="w-14 h-14 flex items-center justify-center bg-amber-100 rounded-full mx-auto mb-4">
            <i className="ri-heart-3-line text-amber-600 text-2xl"></i>
          </div>
          <h3 className="text-xl font-black text-gray-900 mb-3">感恩父母，珍惜当下</h3>
          <p className="text-gray-600 text-sm leading-relaxed max-w-2xl mx-auto">
            看完这些故事，你是否想起了父母每天早起为你准备早餐的身影？
            是否想起了他们省吃俭用供你上学的辛苦？
            无论城市还是农村，父母的爱都是一样的深沉。
            让我们一起感恩，一起努力，不辜负这份爱。
          </p>
        </div>
      </div>
    </section>
  );
}
